/**
 * Computes the Levenshtein distance between two strings.
 */
function levenshteinDistance(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];

    // Increment along the first column of each row
    for (let i = 0; i <= b.length; i++) {
        matrix[i] = [i];
    }

    // Increment each column in the first row
    for (let j = 0; j <= a.length; j++) {
        matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1,     // insertion
                    matrix[i - 1][j] + 1      // deletion
                );
            }
        }
    }

    return matrix[b.length][a.length];
}

/**
 * Calculates a similarity score between 0 and 1.
 */
export function calculateSimilarity(a, b) {
    const distance = levenshteinDistance(a, b);
    const maxLength = Math.max(a.length, b.length);
    if (maxLength === 0) return 1.0;
    return 1.0 - distance / maxLength;
}

/**
 * Finds segments in the project that are similar to the sourceText.
 * @param {string} sourceText - The text to match against.
 * @param {string[]} segments - All source segments in the project.
 * @param {number} currentIndex - The index of the segment currently being edited (to exclude it from matches).
 * @param {string[]} translatedSections - Current translations to check if they differ.
 * @param {string} newTranslation - The new translation being proposed.
 * @param {number} threshold - Similarity threshold (1.0 for exact match).
 * @returns {Array} List of matching segment indices and their details.
 */
export function findSimilarSegments(sourceText, segments, currentIndex, translatedSections, newTranslation, threshold = 1.0) {
    const matches = [];
    if (!sourceText || !segments) return matches;

    const normalizedSource = sourceText.trim();

    segments.forEach((segment, index) => {
        if (index === currentIndex) return;

        const normalizedSegment = (segment || '').trim();
        let similarity = 0;

        if (threshold >= 1.0) {
            if (normalizedSegment === normalizedSource) {
                similarity = 1.0;
            }
        } else {
            similarity = calculateSimilarity(normalizedSource, normalizedSegment);
        }

        if (similarity >= threshold) {
            // Only include if the current translation is different from the new one
            const currentTranslation = (translatedSections[index] || '').trim();
            if (currentTranslation !== newTranslation.trim()) {
                matches.push({
                    index,
                    source: segment,
                    currentTranslation: translatedSections[index] || '',
                    similarity
                });
            }
        }
    });

    return matches;
}
